/**
 * Announcement modal — the "here is what we shipped" dialog, shown once
 * per sign-in session.
 *
 * Deliberately spare: the monogram, a line of gold, a serif headline,
 * one sentence, and a way out. An earlier version put a screenshot across
 * the top — busy, illegible at that size, and it read as clutter rather
 * than craft. The announcement is one sentence; the design should not be
 * louder than the thing it is announcing.
 *
 * The brand mark does the lifting instead of ornament: the Hooten Young
 * monogram, in gold, on the cream itself.
 *
 * Any close counts as seeing it: the primary action, "Got it" / "Next",
 * the ×, Escape, and a backdrop click all acknowledge. An announcement
 * is news, not consent — trapping someone until they hit the one blessed
 * button buys nothing, and What's New keeps the record anyway.
 *
 * "Back" is the exception, and the only control that acknowledges
 * nothing: it re-opens the previous one for a second look, which the
 * reader already passed and already acknowledged.
 *
 * Entrance is a short spring with the contents staggered in behind it.
 * `useReducedMotion` collapses it to a plain fade, matching the
 * treatment in MomentumPanel.
 */

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Dialog, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import type { AckVia } from '../../api/announcements';
import type { Announcement } from '../../api/announcements';
import { colors, docLeading, uiType } from '../../theme';

const GOLD = colors.goldDark;
const MotionBox = motion.create(Box);

/** Art-deco corner bracket, as used on the landing-page section cards. */
function CornerBracket({ top, left }: { top: boolean; left: boolean }) {
  const inset = 14;
  const size = 15;
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        ...(top ? { top: inset } : { bottom: inset }),
        ...(left ? { left: inset } : { right: inset }),
        width: size,
        height: size,
        pointerEvents: 'none',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          bgcolor: GOLD,
          opacity: 0.45,
        },
        '&::before': {
          ...(top ? { top: 0 } : { bottom: 0 }),
          ...(left ? { left: 0 } : { right: 0 }),
          width: '100%',
          height: '1px',
        },
        '&::after': {
          ...(top ? { top: 0 } : { bottom: 0 }),
          ...(left ? { left: 0 } : { right: 0 }),
          width: '1px',
          height: '100%',
        },
      }}
    />
  );
}

/**
 * A quiet text control for moving through a run.
 *
 * Disabled rather than hidden on the first page: a button that vanishes
 * shifts everything beside it, and the reader loses their place in a
 * dialog that is already asking for their attention.
 */
function StepButton({
  onClick,
  disabled,
  emphasis,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  /** The forward control leads, so it carries a little more weight. */
  emphasis?: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        px: 1,
        py: 0.5,
        border: 'none',
        bgcolor: 'transparent',
        color: disabled ? colors.textMuted : emphasis ? GOLD : colors.textMuted,
        opacity: disabled ? 0.35 : 1,
        font: 'inherit',
        fontSize: uiType.caption,
        fontWeight: emphasis ? 700 : 600,
        letterSpacing: '0.02em',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'color 0.15s, opacity 0.15s',
        '&:hover': disabled ? {} : { color: colors.textPrimary },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * "Where is it?" for the Ask ABHI announcement: a little page with the real
 * launcher's look sitting in its bottom-right corner, and the shortcut.
 */
function WhereToFindAskAbhi() {
  const reduceMotion = useReducedMotion();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
      <Box
        aria-hidden
        sx={{
          position: 'relative',
          width: 132,
          height: 84,
          flexShrink: 0,
          borderRadius: '10px',
          bgcolor: '#fff',
          border: `1px solid ${GOLD}33`,
          boxShadow: '0 6px 16px -8px rgba(15, 10, 7, 0.25)',
          overflow: 'hidden',
        }}
      >
        {/* A sketch of a page: header bar and a few content lines. */}
        <Box sx={{ height: 12, bgcolor: `${GOLD}14`, borderBottom: `1px solid ${GOLD}22` }} />
        {[64, 92, 48].map((w, i) => (
          <Box
            key={i}
            sx={{
              height: 5,
              width: w,
              ml: 1.25,
              mt: i ? 0.75 : 1.25,
              borderRadius: 3,
              bgcolor: 'rgba(15,10,7,0.08)',
            }}
          />
        ))}
        {/* The launcher, bottom-right, with a soft pulse to draw the eye. */}
        <MotionBox
          animate={
            reduceMotion
              ? undefined
              : { boxShadow: ['0 0 0 0 rgba(124,32,244,0.35)', '0 0 0 6px rgba(124,32,244,0)'] }
          }
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
          sx={{
            position: 'absolute',
            right: 7,
            bottom: 7,
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            height: 18,
            pl: '2px',
            pr: '6px',
            borderRadius: 999,
            bgcolor: '#fff',
            border: '1px solid rgba(124, 32, 244, 0.35)',
          }}
        >
          <Box
            component="img"
            src="/ask-abhi/logo-mark.svg"
            alt=""
            sx={{ width: 13, height: 13 }}
          />
          <Box
            component="span"
            sx={{ fontSize: 8, fontWeight: 700, color: colors.textPrimary, whiteSpace: 'nowrap' }}
          >
            Ask ABHI
          </Box>
        </MotionBox>
      </Box>
      <Box sx={{ textAlign: 'left' }}>
        <Typography
          sx={{
            fontSize: uiType.body,
            fontWeight: 700,
            color: colors.textPrimary,
            lineHeight: 1.35,
          }}
        >
          Bottom-right of every page
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: uiType.caption, color: colors.textSecondary }}>
          or press{' '}
          <Box
            component="kbd"
            sx={{
              px: 0.6,
              py: 0.1,
              borderRadius: '4px',
              border: `1px solid ${GOLD}55`,
              bgcolor: '#fff',
              fontFamily: 'inherit',
              fontSize: '0.92em',
            }}
          >
            ⌘J
          </Box>{' '}
          /{' '}
          <Box
            component="kbd"
            sx={{
              px: 0.6,
              py: 0.1,
              borderRadius: '4px',
              border: `1px solid ${GOLD}55`,
              bgcolor: '#fff',
              fontFamily: 'inherit',
              fontSize: '0.92em',
            }}
          >
            Ctrl J
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}

export function AnnouncementModal({
  announcement,
  onDismiss,
  index,
  total,
  onNext,
  onBack,
  canGoBack,
  preview = false,
}: {
  announcement: Announcement;
  onDismiss: (via: AckVia) => void;
  /** Admin preview: the action button closes instead of navigating. */
  preview?: boolean;
  /** 1-based position in the run. */
  index: number;
  /** How many are unseen. 1 hides the counter and reads "Got it". */
  total: number;
  /** Acknowledge this one and move to the next, or close if it is last. */
  onNext: () => void;
  /** Step back to re-read the previous one. Acknowledges nothing. */
  onBack: () => void;
  /** False on the first page. */
  canGoBack: boolean;
}) {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const open = (to: string) => {
    onDismiss('opened');
    if (!preview) navigate(to);
  };

  const many = total > 1;
  const isLast = index >= total;
  const askAbhi = announcement.mark === 'ask_abhi';

  const step = reduceMotion ? 0 : 0.06;
  const rise = (index: number) =>
    reduceMotion
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.2 } }
      : {
          initial: { opacity: 0, y: 8 },
          animate: { opacity: 1, y: 0 },
          transition: { delay: 0.14 + index * step, duration: 0.44, ease: [0.22, 1, 0.36, 1] },
        };

  return (
    <Dialog
      open
      onClose={() => onDismiss('dismissed')}
      maxWidth="xs"
      fullWidth
      // The paper animates itself, so MUI's own Grow would fight it.
      transitionDuration={0}
      aria-labelledby="announcement-title"
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          bgcolor: colors.brandCream,
          border: `1px solid ${GOLD}3d`,
          boxShadow: '0 30px 70px -16px rgba(15, 10, 7, 0.4)',
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15, 10, 7, 0.6)' } } }}
    >
      <MotionBox
        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.95, y: 12 }}
        animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
        transition={
          reduceMotion
            ? { duration: 0.2 }
            : { type: 'spring', stiffness: 250, damping: 25, mass: 0.9 }
        }
        sx={{ position: 'relative', px: { xs: 3.5, sm: 5 }, pt: 4.25, pb: 3.5 }}
      >
        {/* Gold hairline along the very top edge. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(to right, transparent, ${GOLD}, transparent)`,
          }}
        />

        {/* Warm halo behind the monogram. Without it the mark reads as
            pasted on; with it the top of the card has some depth. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: -40,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 260,
            height: 160,
            pointerEvents: 'none',
            background: askAbhi
              ? 'radial-gradient(ellipse at center, rgba(124, 32, 244, 0.14) 0%, transparent 70%)'
              : `radial-gradient(ellipse at center, ${GOLD}1f 0%, transparent 70%)`,
          }}
        />

        {/* ---- Brand mark ----
             The monogram in gold, on the cream itself. It first sat in a
             dark disc, which put a heavy black blob in the middle of a
             warm, light card — the contrast fought the paper instead of
             belonging to it.

             The asset is a white silhouette, so it is used as a MASK over
             a gold fill rather than tinted with filters: one colour
             token, no filter maths to re-derive if the gold ever moves. */}
        <MotionBox {...rise(0)} sx={{ display: 'flex', justifyContent: 'center' }}>
          {askAbhi ? (
            <Box
              component="img"
              src="/ask-abhi/logo-mark.svg"
              alt=""
              aria-hidden
              sx={{ width: 56, height: 56, display: 'block' }}
            />
          ) : (
            <Box
              aria-hidden
              sx={{
                // 105x146 in the source; kept in proportion.
                width: 31,
                height: 43,
                bgcolor: GOLD,
                maskImage: 'url(/brand/hy-monogram-white.png)',
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskImage: 'url(/brand/hy-monogram-white.png)',
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
              }}
            />
          )}
        </MotionBox>

        {/* ---- Eyebrow, flanked by hairlines ---- */}
        <MotionBox
          {...rise(1)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.25,
            mt: 2,
          }}
        >
          {/* The hairlines go on the widest screens only. The platform's
              full name is thirty-two letterspaced capitals, and on a
              phone the rules would either push it onto two lines or
              force the tracking down until it stopped reading as an
              eyebrow. The line is decoration; the name is not. */}
          <Box
            aria-hidden
            sx={{
              display: { xs: 'none', sm: 'block' },
              width: 22,
              height: '1px',
              bgcolor: GOLD,
              opacity: 0.5,
            }}
          />
          <Typography
            sx={{
              fontSize: uiType.micro,
              fontWeight: 700,
              // Tightened from 0.2em: the longer name needs the room, and
              // at this size the difference is imperceptible.
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: GOLD,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            New in Hooten Young Ops Platform
          </Typography>
          <Box
            aria-hidden
            sx={{
              display: { xs: 'none', sm: 'block' },
              width: 22,
              height: '1px',
              bgcolor: GOLD,
              opacity: 0.5,
            }}
          />
        </MotionBox>

        <MotionBox {...rise(2)}>
          <Typography
            id="announcement-title"
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: { xs: uiType.headline, sm: 28 },
              fontWeight: 600,
              color: colors.textPrimary,
              textAlign: 'center',
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              mt: 1.25,
            }}
          >
            {announcement.title}
          </Typography>
        </MotionBox>

        <MotionBox {...rise(3)}>
          <Typography
            sx={{
              fontSize: uiType.body,
              lineHeight: docLeading.relaxed,
              color: colors.textSecondary,
              textAlign: 'center',
              mt: 1.25,
              mx: 'auto',
              maxWidth: 305,
            }}
          >
            {announcement.body}
          </Typography>
        </MotionBox>

        {askAbhi && (
          <MotionBox {...rise(3)} sx={{ mt: 2.5 }}>
            <WhereToFindAskAbhi />
          </MotionBox>
        )}

        {/* Actions.
            Stacked, not side by side. Two buttons on one row read as a
            choice between equals; there is only one thing to do here,
            and "Got it" is the way out rather than the alternative. */}
        <MotionBox {...rise(4)} sx={{ mt: 3.25 }}>
          {announcement.action && (
            <Box
              component="button"
              type="button"
              onClick={() => open(announcement.action!.to)}
              sx={{
                display: 'block',
                width: '100%',
                px: 3,
                py: 1.3,
                border: 'none',
                borderRadius: 999,
                background: `linear-gradient(135deg, ${colors.gold} 0%, ${GOLD} 100%)`,
                color: '#fff',
                font: 'inherit',
                fontSize: uiType.body,
                fontWeight: 700,
                letterSpacing: '0.01em',
                cursor: 'pointer',
                boxShadow: `0 8px 20px -10px ${GOLD}, inset 0 1px 0 rgba(255,255,255,0.28)`,
                transition: 'filter 0.15s, box-shadow 0.15s',
                '&:hover': {
                  filter: 'brightness(1.05)',
                  boxShadow: `0 12px 26px -10px ${GOLD}, inset 0 1px 0 rgba(255,255,255,0.32)`,
                },
              }}
            >
              {announcement.action.label}
            </Box>
          )}
          {/* One announcement keeps the plain centred way out. A run
              needs Back as well, so it becomes a three-part row — the
              only layout where "where am I" and "how do I move" sit
              together. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: many ? 'space-between' : 'center',
              mt: announcement.action ? 1.5 : 0,
            }}
          >
            {many && (
              <StepButton onClick={onBack} disabled={!canGoBack}>
                Back
              </StepButton>
            )}
            <StepButton onClick={onNext} emphasis={many}>
              {many && !isLast ? 'Next' : 'Got it'}
            </StepButton>
          </Box>
        </MotionBox>

        {/* Where you are in the run. Dots rather than "1 of 3": it is a
            progress cue, not a number anyone needs to read. Hidden
            entirely when there is only one, so the common case carries no
            extra furniture. */}
        {many && (
          <MotionBox
            {...rise(5)}
            sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 2.5 }}
            role="img"
            aria-label={`Announcement ${index} of ${total}`}
          >
            {Array.from({ length: total }, (_, i) => (
              <Box
                key={i}
                aria-hidden
                sx={{
                  width: i === index - 1 ? 16 : 5,
                  height: 5,
                  borderRadius: 3,
                  bgcolor: i === index - 1 ? GOLD : `${GOLD}40`,
                  transition: 'width 220ms ease, background-color 220ms ease',
                }}
              />
            ))}
          </MotionBox>
        )}

        {/* Three, not four — the dismiss button owns the top-right
            corner, and a bracket behind it just looks like a collision. */}
        <CornerBracket top left />
        <CornerBracket top={false} left />
        <CornerBracket top={false} left={false} />
      </MotionBox>

      <Box
        component="button"
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss('dismissed')}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0,
          border: 'none',
          borderRadius: '50%',
          bgcolor: 'transparent',
          color: colors.textMuted,
          cursor: 'pointer',
          transition: 'background-color 0.15s, color 0.15s',
          '&:hover': { bgcolor: `${GOLD}1a`, color: GOLD },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 17 }} />
      </Box>
    </Dialog>
  );
}
